// AI agent population for labor-mix charts, sampled from the Plan A model
// spreadsheet ground truth (data/plan-a/agentGroundTruth.ts: copies, per-copy
// speed in tok/s, and AI R&D uplift from the [wip]plan-a-side-panel rows).
// Indexed by YEAR; the old power-law-vs-compute fit (against the hand-tuned
// V4 timeline) is retired — it ran ~3-20x hot on speed and uplift.

import { getCapabilityStageLabelForPlanValue } from "@/app/playbook/extrapolated-graph/capabilityThresholds"
import {
  PLAN_A_COPIES,
  PLAN_A_COPIES_SPECIALIZED,
  PLAN_A_RD_UPLIFT,
  PLAN_A_SPEED_TOKS,
  PLAN_A_SPEED_TOKS_SPECIALIZED,
  sampleLog,
  sampleLogUntilSeriesEnd,
} from "@/data/plan-a/agentGroundTruth"

export type ComputeDerivedAgentData = {
  // The general-hardware fleet (most copies) and its per-copy speed.
  copies: number
  speed: number
  // The inference-specialized fleet: a small share of copies running much
  // faster ("up to" speed). The sheet only defines the split 2026-2040;
  // outside that window speedSpecialized is null and copiesSpecialized 0.
  copiesSpecialized: number
  speedSpecialized: number | null
  rdMultiple: number
  thresholdLabel: string
}

// Ground-truth agent data for a (decimal) year. Returns the agent population,
// per-copy speed (tok/s), and R&D multiplier the playbook UI expects.
export const deriveAgentDataForYear = (
  year: number,
): ComputeDerivedAgentData => {
  if (!isFinite(year) || year < 2025) {
    return {
      copies: 0, speed: 0, copiesSpecialized: 0, speedSpecialized: null,
      rdMultiple: 0, thresholdLabel: "AI Research Assistant",
    }
  }
  const copies = sampleLog(PLAN_A_COPIES, year)
  const speed = sampleLog(PLAN_A_SPEED_TOKS, year)
  const speedSpecialized = sampleLogUntilSeriesEnd(PLAN_A_SPEED_TOKS_SPECIALIZED, year)
  const copiesSpecialized = speedSpecialized === null
    ? 0
    : sampleLog(PLAN_A_COPIES_SPECIALIZED, year)
  const rdMultiple = sampleLog(PLAN_A_RD_UPLIFT, year)
  return {
    copies, speed, copiesSpecialized, speedSpecialized,
    rdMultiple, thresholdLabel: getThresholdLabel(rdMultiple),
  }
}

const getThresholdLabel = (rdMultiple: number): string => {
  const label = getCapabilityStageLabelForPlanValue(rdMultiple, "A")
  return label.startsWith("Below ") ? "AI Research Assistant" : label
}
